import { CSSProperties } from "react";
import { PartialBrandTheme, PartialQuestThemeData } from "./logintypes";

interface LoginStyleConfig {
  Form?: CSSProperties;
  Heading?: CSSProperties;
  Description?: CSSProperties;
  Label?: CSSProperties;
  Input?: CSSProperties;
  OtpInput?: CSSProperties;
  PrimaryButton?: CSSProperties;
  SecondaryButton?: CSSProperties;
  IconStyle?: {
    color?: string;
    Background?: string;
    BorderColor?: string;
  };
  Footer?: {
    FooterStyle?: CSSProperties;
    FooterTextStyle?: CSSProperties;
    FooterIcon?: CSSProperties;
    FooterText?: string;
    FooterLink?: string;
  };
}

interface OrDividerStyles {
  color?: string;
  line?: {
    backgroundColor?: string;
  };
}

class LoginThemeManager {
  private themeConfig: any;
  private styleConfig: LoginStyleConfig;
  private questThemeData: PartialQuestThemeData;
  private brandTheme: PartialBrandTheme;
  private accent: string;
  constructor(
    themeConfig = {},
    styleConfig: LoginStyleConfig = {},
    questThemeData?: PartialQuestThemeData,
    brandTheme?: PartialBrandTheme,
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = questThemeData || {
      accentColor: "",
      theme: "",
      borderRadius: "",
      buttonColor: "",
      images: [],
    };
    this.brandTheme = brandTheme || {
      accentColor: "",
      background: "",
      borderRadius: "",
      primaryButton: "",
      contentColor: "",
      fontFamily: "",
      logo: "",
      primaryColor: "",
      secondaryColor: "",
      tertiaryColor: "",
      titleColor: "",
    };
  }

  // Main container styles
  getContainerStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.Form?.backgroundColor ||
        this.themeConfig?.backgroundColor,
      height: this.styleConfig?.Form?.height || "auto",
      fontFamily:
        this.styleConfig?.Form?.fontFamily ||
        this.brandTheme?.fontFamily ||
        this.themeConfig.fontFamily ||
        "'Figtree', sans-serif",
      borderRadius:
        this.styleConfig?.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius,
      ...this.styleConfig?.Form,
    };
  }

  // Heading styles
  getHeadingStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Heading,
    };
  }

  // Description styles
  getDescriptionStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Description,
    };
  }

  // Label styles
  getLabelStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Label?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Label,
    };
  }

  // Input styles
  getInputStyles(): CSSProperties {
    return {
      borderColor:
        this.styleConfig?.Input?.borderColor || this.themeConfig?.borderColor,
      color:
        this.styleConfig?.Input?.color ||
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      borderRadius: "6px",
      borderWidth: "1.5px",
      padding: "8px 12px",
      ...this.styleConfig?.Input,
    };
  }

  // OTP Input styles
  getOtpInputStyles(): CSSProperties {
    return {
      backgroundColor:
        this.styleConfig?.OtpInput?.backgroundColor || "transparent",
      borderColor:
        this.styleConfig?.OtpInput?.borderColor || this.themeConfig.borderColor,
      color:
        this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig.primaryColor,
      ...this.styleConfig?.OtpInput,
    };
  }

  getPrimaryButtonStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.PrimaryButton?.background ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this?.accent ||
        this.themeConfig?.buttonColor,
      fontFamily: this.themeConfig.fontFamily || "'Figtree', sans-serif",
      border: '0px',
      ...this.styleConfig?.PrimaryButton,
    };
  }

  getSecondaryButtonStyles(): CSSProperties {
    return {
      borderColor:
        this.styleConfig?.SecondaryButton?.borderColor ||
        this.themeConfig?.borderColor,
      background: this.styleConfig?.SecondaryButton?.backgroundColor,
      color:
        this.styleConfig?.SecondaryButton?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor || '#2c2c2c',
      ...this.styleConfig?.SecondaryButton,
    };
  }

  getIconStyles() {
    const background =
      this.styleConfig?.IconStyle?.Background ||
      this.brandTheme?.primaryColor ||
      this.questThemeData?.accentColor ||
      this.accent;

    const defaultColor =
      this.styleConfig?.IconStyle?.color ||
      this.brandTheme?.titleColor ||
      "#F4EBFF";

    const getLuminance = (color) => {
      let c = color?.replace("#", "");
      if (c.length === 3) c = c.split("").map((x) => x + x).join("");
      const [r, g, b] = [
        parseInt(c.substr(0, 2), 16),
        parseInt(c.substr(2, 2), 16),
        parseInt(c.substr(4, 2), 16),
      ];
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    };

    const bgLuminance = background ? getLuminance(background) : 1;
    const isDark = bgLuminance < 0.4;

    return {
      background,
      borderColor:
        this.styleConfig?.IconStyle?.BorderColor ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this.accent,
      color: isDark ? "#FFFFFF" : defaultColor,
    };
  }

  // Footer styles
  getFooterStyles() {
    return {
      container: {
        background:
          this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
          this.styleConfig?.Form?.backgroundColor ||
          this.themeConfig?.backgroundColor,
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      text: this.styleConfig?.Footer?.FooterTextStyle || {},
      icon: this.styleConfig?.Footer?.FooterIcon || {},
      content: {
        text: this.styleConfig?.Footer?.FooterText,
        link: this.styleConfig?.Footer?.FooterLink,
      },
    };
  }

  getOrDividerStyles(): OrDividerStyles {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.secondaryColor ||
        this.themeConfig?.secondaryColor,
      line: {
        backgroundColor:
          this.styleConfig?.Description?.color ||
          this.brandTheme?.secondaryColor ||
          this.themeConfig?.secondaryColor,
      },
    };
  }

  getORTextStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.Description?.color ||
        this.brandTheme?.secondaryColor ||
        this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Description,
    };
  }

  // Update methods
  updateThemeConfig(newConfig: any) {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<LoginStyleConfig>) {
    this.styleConfig = { ...this.styleConfig, ...newConfig };
  }

  updateQuestTheme(newTheme: PartialQuestThemeData) {
    this.questThemeData = { ...this.questThemeData, ...newTheme };
  }

  updateBrandTheme(newTheme: PartialBrandTheme) {
    this.brandTheme = { ...this.brandTheme, ...newTheme };
  }
}

export default LoginThemeManager;
